document.addEventListener('DOMContentLoaded', () => {
  const config = window.napConfiguratorData || {};
  const shopLocale = config.shopLocale || document.documentElement.lang || 'en';
  const currencyCode =
    window.Shopify?.currency?.active ||
    config.currencyCode ||
    'USD';  
  const sectionFetchUrl = config.sectionFetchUrl || window.location.pathname;
  const routesRoot = window.Shopify?.routes?.root || '/';
  const cartAddUrl = `${routesRoot}cart/add.js`;
  const cartChangeUrl = `${routesRoot}cart/change.js`;

  const state = {
    bundle: 'single',
    covers: 0,
    stands: 0,
    cableKit: 0,
    controlRecords: 0,
    isSubmitting: false,
    isUpdatingCart: false
  };

  const root = document.querySelector('[data-nap-configurator]') || document;

  const subtotalEl = root.querySelector('[data-subtotal]');
  const bundleInputs = root.querySelectorAll('input[name="bundle"]');
  const bundleOptions = root.querySelectorAll('[data-bundle-option]');

  const qtyInputs = {
    covers: root.querySelector('[data-qty="covers"]'),
    stands: root.querySelector('[data-qty="stands"]'),
    cableKit: root.querySelector('[data-qty="cable_kit"]'),
    controlRecords: root.querySelector('[data-qty="control_records"]')
  };

  const addToCartBtn = root.querySelector('[data-add-to-cart]');
  const statusEl = root.querySelector('[data-configurator-status]');

  function toNumber(value) {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  }

  function clampQty(value) {
    return Math.max(0, parseInt(value, 10) || 0);
  }

  function getCurrencyCode() {
    return currencyCode;
  } 

  function formatPrice(cents) {
    return new Intl.NumberFormat(shopLocale, {
      style: 'currency',
      currency: getCurrencyCode()
    }).format(toNumber(cents) / 100);
  }

  async function refreshInlineCart() {
    const inlineCart = document.querySelector('[data-inline-cart]');
    if (!inlineCart) return;

    const response = await fetch(sectionFetchUrl, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });

    if (!response.ok) {
      throw new Error('Could not refresh section');
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newInlineCart = doc.querySelector('[data-inline-cart]');

    if (newInlineCart) {
      inlineCart.innerHTML = newInlineCart.innerHTML;
    }
  }

  function getBundleConfig() {
    return config.bundles?.[state.bundle] || { price: 0, label: '', variantId: null };
  }

  function getBundlePrice() {
    return toNumber(getBundleConfig().price);
  }

  function getAddonUnitPrice(key) {
    return toNumber(config.addons?.[key]?.price ?? config.addons?.[key]);
  }

  function getAddonVariantId(key) {
    return config.addons?.[key]?.variantId ?? null;
  }

  function getAddonsTotal() {
    return (
      getAddonUnitPrice('covers') * state.covers +
      getAddonUnitPrice('stands') * state.stands +
      getAddonUnitPrice('cable_kit') * state.cableKit +
      getAddonUnitPrice('control_records') * state.controlRecords
    );
  }

  function setStatus(message, isError = false) {
    if (!statusEl) return;
    statusEl.textContent = message || '';
    statusEl.dataset.state = isError ? 'error' : 'default';
  }

  function updateBundleUI() {
    bundleInputs.forEach((input) => {
      input.checked = input.value === state.bundle;
    });

    bundleOptions.forEach((option) => {
      option.classList.toggle('is-selected', option.dataset.bundleOption === state.bundle);
    });
  }

  function updateSubtotal() {
    const totalCents = getBundlePrice() + getAddonsTotal();

    if (subtotalEl) {
      subtotalEl.textContent = formatPrice(totalCents);
    }

    updateBundleUI();
  }

  function setBundle(bundleKey) {
    state.bundle = bundleKey;
    updateSubtotal();
  }

  function syncInitialBundle() {
    const checked = root.querySelector('input[name="bundle"]:checked');
    if (checked) {
      state.bundle = checked.value;
    }
  }

  function syncInitialQtyInputs() {
    if (qtyInputs.covers) state.covers = clampQty(qtyInputs.covers.value);
    if (qtyInputs.stands) state.stands = clampQty(qtyInputs.stands.value);
    if (qtyInputs.cableKit) state.cableKit = clampQty(qtyInputs.cableKit.value);
    if (qtyInputs.controlRecords) state.controlRecords = clampQty(qtyInputs.controlRecords.value);
  }

  function bindBundleInputs() {
    bundleInputs.forEach((input) => {
      input.addEventListener('change', () => {
        setBundle(input.value);
      });
    });
  }

  function bindBundleOptions() {
    bundleOptions.forEach((option) => {
      option.addEventListener('click', () => {
        const bundleKey = option.dataset.bundleOption;
        if (!bundleKey) return;

        const matchingInput = root.querySelector(`input[name="bundle"][value="${bundleKey}"]`);
        if (matchingInput) {
          matchingInput.checked = true;
        }

        setBundle(bundleKey);
      });
    });
  }

  function bindQtyInput(input, stateKey) {
    if (!input) return;

    const sync = (event) => {
      const qty = clampQty(event.target.value);
      event.target.value = qty;
      state[stateKey] = qty;
      updateSubtotal();
    };

    input.addEventListener('input', sync);
    input.addEventListener('change', sync);
  }

  function buildCartItems() {
    const items = [];
    const bundleConfig = getBundleConfig();
    const bundleVariantId = bundleConfig.variantId;

    if (bundleVariantId) {
      items.push({
        id: bundleVariantId,
        quantity: 1,
        properties: {
          _nap_bundle: bundleConfig.label || state.bundle
        }
      });
    }

    const addonMap = [
      { key: 'covers', qty: state.covers },
      { key: 'stands', qty: state.stands },
      { key: 'cable_kit', qty: state.cableKit },
      { key: 'control_records', qty: state.controlRecords }
    ];

    addonMap.forEach(({ key, qty }) => {
      const variantId = getAddonVariantId(key);
      if (variantId && qty > 0) {
        items.push({
          id: variantId,
          quantity: qty,
          properties: {
            _nap_bundle: bundleConfig.label || state.bundle
          }
        });
      }
    });

    return items;
  }

  async function addToCart() {
    const items = buildCartItems();

    if (!items.length) {
      setStatus('No items selected.', true);
      return;
    }

    if (state.isSubmitting) return;

    state.isSubmitting = true;

    if (addToCartBtn) {
      addToCartBtn.disabled = true;
    }

    setStatus('Adding to cart…');

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        throw new Error('Cart request failed');
      }

      await response.json();
      await refreshInlineCart();
      setStatus('Added to cart.');
      document.dispatchEvent(new CustomEvent('nap:cart-added', { detail: { items } }));
    } catch (error) {
      setStatus('Could not add to cart. Please try again.', true);
    } finally {
      state.isSubmitting = false;

      if (addToCartBtn) {
        addToCartBtn.disabled = false;
      }
    }
  }

  async function updateCartLine(line, quantity) {
    const response = await fetch(cartChangeUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        line: Number(line),
        quantity: Number(quantity)
      })
    });

    if (!response.ok) {
      throw new Error('Cart line update failed');
    }

    await response.json();
    await refreshInlineCart();
  }

  function bindCartQuantityAjax() {
    document.addEventListener('change', async (event) => {
      const qtyInput = event.target.closest('.nap-cart-qty');
      if (!qtyInput) return;
      if (state.isUpdatingCart) return;

      const line = qtyInput.dataset.cartLine;
      if (!line) return;

      const quantity = clampQty(qtyInput.value);
      qtyInput.value = quantity;
      qtyInput.disabled = true;
      state.isUpdatingCart = true;

      try {
        await updateCartLine(line, quantity);
        setStatus('');
      } catch (error) {
        setStatus('Could not update cart. Please try again.', true);
        qtyInput.disabled = false;
      } finally {
        state.isUpdatingCart = false;
      }
    });
  }

  if (addToCartBtn) {
    addToCartBtn.addEventListener('click', (event) => {
      event.preventDefault();
      addToCart();
    });
  }

  syncInitialBundle();
  syncInitialQtyInputs();

  bindBundleInputs();
  bindBundleOptions();
  bindQtyInput(qtyInputs.covers, 'covers');
  bindQtyInput(qtyInputs.stands, 'stands');
  bindQtyInput(qtyInputs.cableKit, 'cableKit');
  bindQtyInput(qtyInputs.controlRecords, 'controlRecords');
  bindCartQuantityAjax();

  updateSubtotal();
});